import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currencyFormatter';
import { 
  ArrowLeft,
  TrendingUp, 
  TrendingDown, 
  RefreshCw,
  Eye,
  EyeOff,
  Plus,
  Minus,
  Download,
  Upload,
  Copy,
  Check,
  AlertTriangle,
  Info,
  Search,
  Filter,
  BarChart3,
  PieChart,
  Clock,
  Star,
  Zap,
  DollarSign
} from 'lucide-react';
import { cryptoAPI, accountsAPI } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { handleApiError } from '../utils/errorHandler';

const CryptoTrading = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('portfolio');
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [orderType, setOrderType] = useState('buy');
  const [amount, setAmount] = useState('');
  const [cryptoAmount, setCryptoAmount] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [showBalances, setShowBalances] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedAddress, setCopiedAddress] = useState('');

  // Data states
  const [portfolio, setPortfolio] = useState([]);
  const [marketData, setMarketData] = useState([]);
  const [marketLoading, setMarketLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [depositMethods, setDepositMethods] = useState([]);
  const [totalPortfolioValue, setTotalPortfolioValue] = useState(0);

  // Modal states
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [selectedDepositMethod, setSelectedDepositMethod] = useState(null);
  const [withdrawData, setWithdrawData] = useState({
      symbol: 'BTC',
    amount: '',
    toAddress: '',
    network: ''
  });
  const [depositData, setDepositData] = useState({
    symbol: 'BTC',
    amount: '',
    txHash: '',
    fromAddress: ''
  });

  const notification = useNotification();

  useEffect(() => {
    fetchInitialData();
    const interval = setInterval(fetchMarketData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [portfolioRes, marketRes, transactionsRes, accountsRes, depositMethodsRes] = await Promise.all([
        cryptoAPI.getPortfolio(),
        cryptoAPI.getMarketData(),
        cryptoAPI.getTransactions({ limit: 20 }),
        accountsAPI.getAccounts(),
        cryptoAPI.getDepositMethods()
      ]);

      if (portfolioRes.status === 'success') {
        setPortfolio(portfolioRes.data.portfolio);
        setTotalPortfolioValue(portfolioRes.data.total_value);
      }

      if (marketRes.status === 'success') {
        setMarketData(marketRes.data.market_data);
      }

      if (transactionsRes.status === 'success') {
        setTransactions(transactionsRes.data.transactions);
      }

      if (accountsRes.status === 'success') {
        setAccounts(accountsRes.data.accounts);
        if (accountsRes.data.accounts.length > 0) {
          setSelectedAccount(accountsRes.data.accounts[0].id);
        }
      }

      if (depositMethodsRes.status === 'success') {
        setDepositMethods(depositMethodsRes.data.deposit_methods);
      }
    } catch (error) {
      handleApiError(error, notification, 'Failed to load crypto data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketData = async () => {
    setMarketLoading(true);
    try {
      // Fetch live market data from CoinGecko API
      const response = await fetch('https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=100&page=1&sparkline=false&locale=en');
      const data = await response.json();
      
      if (data && Array.isArray(data)) {
        const formattedData = data.map(coin => ({
          symbol: coin.symbol.toUpperCase(),
          name: coin.name,
          price: coin.current_price,
          change24h: coin.price_change_percentage_24h,
          marketCap: coin.market_cap,
          volume24h: coin.total_volume,
          image: coin.image,
          lastUpdated: coin.last_updated
        }));
        setMarketData(formattedData);
      }
    } catch (error) {
      console.error('Failed to refresh market data:', error);
      // Fallback to mock data if API fails
      const fallbackData = [
        {
          symbol: 'BTC',
          name: 'Bitcoin',
          price: 45000,
          change24h: 2.5,
          marketCap: 850000000000,
          volume24h: 25000000000,
          image: 'https://assets.coincap.io/assets/icons/btc@2x.png',
          lastUpdated: new Date().toISOString()
        },
        {
          symbol: 'ETH',
          name: 'Ethereum',
          price: 3200,
          change24h: -1.2,
          marketCap: 380000000000,
          volume24h: 18000000000,
          image: 'https://assets.coincap.io/assets/icons/eth@2x.png',
          lastUpdated: new Date().toISOString()
        }
      ];
      setMarketData(fallbackData);
    } finally {
      setMarketLoading(false);
    }
  };

  // Crypto icons with proper logos from CoinCap API
  const getCryptoIcon = (symbol) => {
    const icons = {
      'BTC': { color: '#F7931A', name: 'Bitcoin' },
      'ETH': { color: '#627EEA', name: 'Ethereum' },
      'BNB': { color: '#F3BA2F', name: 'BNB' },
      'ADA': { color: '#0033AD', name: 'Cardano' },
      'SOL': { color: '#9945FF', name: 'Solana' },
      'USDC': { color: '#2775CA', name: 'USD Coin' },
      'USDT': { color: '#26A17B', name: 'Tether' },
      'DOGE': { color: '#C2A633', name: 'Dogecoin' },
      'DOT': { color: '#E6007A', name: 'Polkadot' },
      'MATIC': { color: '#8247E5', name: 'Polygon' }
    };
    return icons[symbol] || { color: '#6B7280', name: symbol };
  };

  const getCryptoLogo = (symbol) => {
    return `https://assets.coincap.io/assets/icons/${symbol.toLowerCase()}@2x.png`;
  };

  // Currency formatter for user's currency
  const formatCurrencyWithUserCurrency = (amount) => {
    return formatCurrency(amount, user?.currency_code || 'USD');
  };

  const formatCrypto = (amount, decimals = 8) => {
    const numAmount = parseFloat(amount) || 0;
    return numAmount.toFixed(decimals);
  };

  const formatLargeNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toFixed(2);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSelectedCryptoPrice = () => {
    const crypto = marketData.find(item => item.symbol === selectedCrypto);
    return crypto?.price || 0;
  };

  const getSelectedCryptoBalance = () => {
    const crypto = portfolio.find(item => item.symbol === selectedCrypto);
    return crypto?.balance || 0;
  };

  const filteredMarketData = marketData.filter(crypto =>
    crypto.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    crypto.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAddress(text);
      setTimeout(() => setCopiedAddress(''), 2000);
      notification.showSuccess('Address copied to clipboard');
    } catch (error) {
      notification.showError('Failed to copy address');
    }
  };

  const handleBuyCrypto = async (e) => {
    e.preventDefault();
    if (!amount || !selectedAccount || !selectedCrypto) return;

    setLoading(true);
    setErrors({});

    try {
      const response = await cryptoAPI.buy({
        symbol: selectedCrypto,
        amount_usd: parseFloat(amount),
        bank_account_id: selectedAccount
      });

      if (response.status === 'success') {
        notification.showSuccess(response.message);
        setAmount('');
        setCryptoAmount('');
        
        // Refresh data
        await fetchInitialData();
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      handleApiError(error, notification, 'Failed to buy cryptocurrency');
    } finally {
      setLoading(false);
    }
  };

  const handleSellCrypto = async (e) => {
    e.preventDefault();
    if (!cryptoAmount || !selectedAccount || !selectedCrypto) return;

    setLoading(true);
    setErrors({});

    try {
      const response = await cryptoAPI.sell({
        symbol: selectedCrypto,
        crypto_amount: parseFloat(cryptoAmount),
        bank_account_id: selectedAccount
      });

      if (response.status === 'success') {
        notification.showSuccess(response.message);
        setAmount('');
        setCryptoAmount('');
        
        // Refresh data
        await fetchInitialData();
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      handleApiError(error, notification, 'Failed to sell cryptocurrency');
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    if (!depositData.symbol || !depositData.amount || !depositData.txHash || !depositData.fromAddress) return;

    setLoading(true);

    try {
      const response = await cryptoAPI.deposit({
        symbol: depositData.symbol,
        amount: parseFloat(depositData.amount),
        tx_hash: depositData.txHash,
        from_address: depositData.fromAddress
      });

      if (response.status === 'success') {
        notification.showSuccess(response.message);
        setShowDepositModal(false);
        setDepositData({
          symbol: 'BTC',
          amount: '',
          txHash: '',
          fromAddress: ''
        });
        
        // Refresh transactions
        const transactionsRes = await cryptoAPI.getTransactions({ limit: 20 });
        if (transactionsRes.status === 'success') {
          setTransactions(transactionsRes.data.transactions);
        }
      }
    } catch (error) {
      handleApiError(error, notification, 'Failed to submit deposit');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!withdrawData.symbol || !withdrawData.amount || !withdrawData.toAddress) return;

    setLoading(true);

    try {
      const response = await cryptoAPI.withdraw({
        symbol: withdrawData.symbol,
        amount: parseFloat(withdrawData.amount),
        to_address: withdrawData.toAddress,
        network: withdrawData.network || withdrawData.symbol
      });

      if (response.status === 'success') {
        notification.showSuccess(response.message);
        setShowWithdrawModal(false);
        setWithdrawData({
          symbol: 'BTC',
          amount: '',
          toAddress: '',
          network: ''
        });
        
        // Refresh data
        await fetchInitialData();
      }
    } catch (error) {
      handleApiError(error, notification, 'Failed to process withdrawal');
    } finally {
      setLoading(false);
    }
  };

  if (loading && portfolio.length === 0) {
  return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-yellow-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading Crypto Trading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link to="/dashboard" className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
                <ArrowLeft className="h-5 w-5 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Back to Dashboard</span>
                <span className="sm:hidden">Back</span>
              </Link>
              <div className="h-6 border-l border-gray-300 hidden sm:block"></div>
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Crypto Trading</h1>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Live
              </span>
                </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={fetchMarketData}
                className="flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''} sm:mr-2`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <button
                onClick={() => setShowBalances(!showBalances)}
                className="flex items-center px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {showBalances ? <EyeOff className="h-4 w-4 sm:mr-2" /> : <Eye className="h-4 w-4 sm:mr-2" />}
                <span className="hidden sm:inline">{showBalances ? 'Hide' : 'Show'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Portfolio Overview */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 space-y-4 sm:space-y-0">
              <div>
                <h2 className="text-base sm:text-lg font-medium opacity-90">Total Portfolio Value</h2>
                <div className="text-2xl sm:text-3xl font-bold">
                  {showBalances ? formatCurrencyWithUserCurrency(totalPortfolioValue) : '••••••'}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => setShowDepositModal(true)}
                  className="flex items-center justify-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors text-sm sm:text-base"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Deposit
                </button>
                <button
                  onClick={() => setShowWithdrawModal(true)}
                  className="flex items-center justify-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors text-sm sm:text-base"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Withdraw
                </button>
                </div>
              </div>
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-xs sm:text-sm opacity-90">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                {portfolio.length} Assets
            </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                Real-time Prices
                </div>
              </div>
                </div>
              </div>

        {/* Navigation Tabs */}
        <div className="mb-4 sm:mb-6">
          <nav className="flex space-x-2 sm:space-x-8 overflow-x-auto pb-2 sm:pb-0">
              {[
                { id: 'portfolio', label: 'Portfolio', icon: PieChart },
              { id: 'market', label: 'Market', icon: BarChart3 },
              { id: 'trade', label: 'Trade', icon: Zap },
                { id: 'history', label: 'History', icon: Clock }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                <tab.icon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
        </div>

        {/* Portfolio Tab */}
            {activeTab === 'portfolio' && (
          <div className="space-y-4 sm:space-y-6">
            {portfolio.length > 0 ? (
              <div className="grid gap-3 sm:gap-4">
                {portfolio.map((asset) => {
                  const cryptoInfo = getCryptoIcon(asset.symbol);
                  const changeColor = asset.change_24h >= 0 ? 'text-green-600' : 'text-red-600';
                  const changeIcon = asset.change_24h >= 0 ? TrendingUp : TrendingDown;
                  const ChangeIcon = changeIcon;

                  return (
              <motion.div
                      key={asset.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-all duration-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
                          <div 
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                            style={{ backgroundColor: `${cryptoInfo.color}20` }}
                          >
                            <img 
                              src={getCryptoLogo(asset.symbol)} 
                              alt={asset.name}
                              className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div 
                              className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-sm sm:text-lg font-bold text-white hidden"
                              style={{ backgroundColor: cryptoInfo.color }}
                            >
                              {asset.symbol.charAt(0)}
                          </div>
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{asset.name}</h3>
                            <p className="text-xs sm:text-sm text-gray-500">{asset.symbol}</p>
                        </div>
                          </div>
                        <div className="text-right space-y-1">
                          <div className="text-sm sm:text-lg font-semibold text-gray-900">
                            {showBalances ? formatCurrencyWithUserCurrency(asset.current_value) : '••••••'}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-500">
                            {showBalances ? `${formatCrypto(asset.balance, 6)} ${asset.symbol}` : '••••••'}
                        </div>
                          <div className="text-sm sm:text-lg font-semibold text-gray-900">
                            {formatCurrencyWithUserCurrency(asset.current_price)}
                      </div>
                          <div className={`flex items-center justify-end text-xs sm:text-sm ${changeColor}`}>
                            <ChangeIcon className="h-3 w-3 mr-1" />
                            {asset.change_24h > 0 ? '+' : ''}{asset.change_24h.toFixed(2)}%
                          </div>
                        </div>
                </div>
              </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12 bg-white rounded-xl border border-gray-200">
                <div className="text-3xl sm:text-4xl mb-4">📊</div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No crypto assets yet</h3>
                <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6 px-4">Start building your crypto portfolio by purchasing your first cryptocurrency.</p>
                <button
                  onClick={() => setActiveTab('trade')}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                >
                  Start Trading
                </button>
              </div>
            )}
          </div>
        )}

        {/* Trade Tab */}
            {activeTab === 'trade' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-sm p-4 sm:p-8"
              >
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Trade Cryptocurrency</h2>
                
            {/* Order Type Toggle */}
            <div className="flex p-1 bg-gray-100 rounded-lg mb-4 sm:mb-6">
                    <button
                      onClick={() => setOrderType('buy')}
                className={`flex-1 py-2 px-3 sm:px-4 rounded-md text-sm font-medium transition-colors ${
                        orderType === 'buy'
                    ? 'bg-green-500 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Buy
                    </button>
                    <button
                      onClick={() => setOrderType('sell')}
                className={`flex-1 py-2 px-3 sm:px-4 rounded-md text-sm font-medium transition-colors ${
                        orderType === 'sell'
                    ? 'bg-red-500 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Sell
                    </button>
                  </div>

            <form onSubmit={orderType === 'buy' ? handleBuyCrypto : handleSellCrypto} className="space-y-4 sm:space-y-6">
              {/* Account Selection */}
                  <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">
                  {orderType === 'buy' ? 'From Account' : 'To Account'}
                </label>
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm sm:text-base"
                  required
                >
                  <option value="">Select Account</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.account_name} - {formatCurrencyWithUserCurrency(account.balance)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Crypto Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">
                  Cryptocurrency
                </label>
                    <select
                      value={selectedCrypto}
                      onChange={(e) => setSelectedCrypto(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm sm:text-base"
                    >
                      {marketData.map((crypto) => (
                        <option key={crypto.symbol} value={crypto.symbol}>
                      {crypto.name} ({crypto.symbol}) - {formatCurrencyWithUserCurrency(crypto.price)}
                        </option>
                      ))}
                    </select>
                {orderType === 'sell' && (
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    Available: {formatCrypto(getSelectedCryptoBalance(), 6)} {selectedCrypto}
                  </p>
                )}
                  </div>

              {/* Amount Input */}
                  <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">
                  {orderType === 'buy' ? `Amount (${user?.currency_code || 'USD'})` : `Amount (${selectedCrypto})`}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                    value={orderType === 'buy' ? amount : cryptoAmount}
                    onChange={(e) => orderType === 'buy' ? setAmount(e.target.value) : setCryptoAmount(e.target.value)}
                    placeholder={orderType === 'buy' ? '0.00' : '0.00000000'}
                    step={orderType === 'buy' ? '0.01' : '0.00000001'}
                    min={orderType === 'buy' ? '1' : '0.00000001'}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-base sm:text-lg font-semibold"
                    required
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        {orderType === 'buy' ? (
                      <span className="text-gray-400 font-medium text-sm">
                        {user?.currency_code === 'USD' ? '$' : 
                         user?.currency_code === 'EUR' ? '€' : 
                         user?.currency_code === 'GBP' ? '£' : 
                         user?.currency_code === 'NGN' ? '₦' : 
                         user?.currency_code === 'GHS' ? '₵' : 
                         user?.currency_code === 'KES' ? 'KSh' : 
                         user?.currency_code === 'ZAR' ? 'R' : 
                         user?.currency_code === 'CAD' ? 'C$' : 
                         user?.currency_code === 'AUD' ? 'A$' : 
                         user?.currency_code === 'JPY' ? '¥' : 
                         user?.currency_code === 'CHF' ? 'CHF' : 
                         user?.currency_code === 'CNY' ? '¥' : 
                         user?.currency_code === 'INR' ? '₹' : 
                         user?.currency_code === 'BRL' ? 'R$' : 
                         user?.currency_code === 'MXN' ? '$' : 
                         user?.currency_code || '$'}
                      </span>
                    ) : (
                      <div className="w-4 h-4 sm:w-5 sm:h-5 overflow-hidden rounded-full">
                        <img 
                          src={getCryptoLogo(selectedCrypto)} 
                          alt={selectedCrypto}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div 
                          className="w-full h-full rounded-full flex items-center justify-center text-xs font-bold text-white hidden"
                          style={{ backgroundColor: getCryptoIcon(selectedCrypto).color }}
                        >
                          {selectedCrypto.charAt(0)}
                    </div>
                  </div>
                    )}
                        </div>
                        </div>
                {orderType === 'buy' && amount && (
                  <p className="text-xs sm:text-sm text-gray-500 mt-2">
                    ≈ {formatCrypto((parseFloat(amount) * 0.99) / getSelectedCryptoPrice(), 6)} {selectedCrypto} (after 1% fee)
                  </p>
                )}
                {orderType === 'sell' && cryptoAmount && (
                  <p className="text-xs sm:text-sm text-gray-500 mt-2">
                    ≈ {formatCurrencyWithUserCurrency((parseFloat(cryptoAmount) * getSelectedCryptoPrice()) * 0.99)} (after 1% fee)
                  </p>
                )}
                {errors.amount_usd && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.amount_usd[0]}</p>
                )}
                {errors.crypto_amount && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.crypto_amount[0]}</p>
                )}
              </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                disabled={loading || !selectedAccount || (!amount && !cryptoAmount)}
                className={`w-full py-3 sm:py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base ${
                      orderType === 'buy'
                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                        : 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700'
                    }`}
                  >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    {orderType === 'buy' ? <Plus className="w-4 h-4 sm:w-5 sm:h-5" /> : <Minus className="w-4 h-4 sm:w-5 sm:h-5" />}
                    <span>
                      {orderType === 'buy' ? 'Buy' : 'Sell'} {selectedCrypto}
                    </span>
                  </>
                )}
                  </button>
                </form>
              </motion.div>
            )}

        {/* Market Tab */}
            {activeTab === 'market' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Search and Filter */}
            <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search cryptocurrencies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>
                <button className="flex items-center justify-center sm:justify-start px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                      </button>
                    </div>
                  </div>

            {/* Market Data */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Cryptocurrency Market</h3>
                    <p className="text-xs sm:text-sm text-gray-500">
                      Live prices updated every 30 seconds
                      {marketData.length > 0 && (
                        <span className="ml-2 text-blue-600">
                          • Last: {new Date(marketData[0]?.lastUpdated).toLocaleTimeString()}
                        </span>
                      )}
                    </p>
                  </div>
                  <button 
                    onClick={fetchMarketData}
                    disabled={marketLoading}
                    className="flex items-center px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${marketLoading ? 'animate-spin' : ''}`} />
                    {marketLoading ? 'Updating...' : 'Refresh'}
                  </button>
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {filteredMarketData.map((crypto) => {
                  const cryptoInfo = getCryptoIcon(crypto.symbol);
                  const changeColor = crypto.change24h >= 0 ? 'text-green-600' : 'text-red-600';
                  const changeBgColor = crypto.change24h >= 0 ? 'bg-green-50' : 'bg-red-50';
                  const changeIcon = crypto.change24h >= 0 ? TrendingUp : TrendingDown;
                  const ChangeIcon = changeIcon;

                  return (
                    <motion.div
                          key={crypto.symbol}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-3 sm:p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedCrypto(crypto.symbol);
                        setActiveTab('trade');
                      }}
                    >
                      <div className="flex items-center justify-between space-x-3">
                        <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
                          <div 
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                            style={{ backgroundColor: `${cryptoInfo.color}20` }}
                          >
                            <img 
                              src={crypto.image || getCryptoLogo(crypto.symbol)} 
                              alt={crypto.name}
                              className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div 
                              className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-sm sm:text-lg font-bold text-white hidden"
                              style={{ backgroundColor: cryptoInfo.color }}
                            >
                              {crypto.symbol.charAt(0)}
                              </div>
                              </div>
                          <div className="min-w-0">
                            <h4 className="text-sm sm:text-lg font-semibold text-gray-900 truncate">{crypto.name}</h4>
                            <p className="text-xs sm:text-sm text-gray-500">{crypto.symbol}</p>
                            </div>
                        </div>
                        <div className="text-right space-y-1">
                          <div className="text-sm sm:text-xl font-bold text-gray-900">
                              {formatCurrencyWithUserCurrency(crypto.price)}
                            </div>
                          <div className={`flex items-center justify-end ${changeColor}`}>
                            <ChangeIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                            <span className="font-medium text-xs sm:text-sm">
                              {crypto.change24h > 0 ? '+' : ''}{crypto.change24h.toFixed(2)}%
                            </span>
                            </div>
                          <div className="text-right text-xs sm:text-sm text-gray-500 hidden sm:block">
                            <div>Vol: {formatCurrencyWithUserCurrency(crypto.volume24h)}</div>
                            <div>Cap: {formatCurrencyWithUserCurrency(crypto.marketCap)}</div>
                          </div>
                        </div>
                        <div className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${changeBgColor} ${changeColor} hidden sm:block`}>
                          {crypto.change24h >= 0 ? 'BULLISH' : 'BEARISH'}
                        </div>
                </div>
              </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
            )}

        {/* History Tab */}
            {activeTab === 'history' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-sm"
              >
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Transaction History</h2>
                </div>
            
            {transactions.length === 0 ? (
              <div className="p-6 sm:p-8 text-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">No Transactions Yet</h3>
                <p className="text-sm sm:text-base text-gray-500">Your crypto transactions will appear here</p>
              </div>
            ) : (
                <div className="divide-y divide-gray-200">
                {transactions.map((transaction, index) => (
                    <motion.div
                    key={transaction.id || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.05 }}
                    className="p-4 sm:p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between space-x-3">
                      <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            transaction.type === 'buy' ? 'bg-green-100 text-green-600' :
                            transaction.type === 'sell' ? 'bg-red-100 text-red-600' :
                          transaction.type === 'deposit' ? 'bg-blue-100 text-blue-600' :
                          transaction.type === 'withdraw' ? 'bg-orange-100 text-orange-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {transaction.type === 'buy' ? <Plus className="w-5 h-5 sm:w-6 sm:h-6" /> : 
                           transaction.type === 'sell' ? <Minus className="w-5 h-5 sm:w-6 sm:h-6" /> :
                           transaction.type === 'deposit' ? <Download className="w-5 h-5 sm:w-6 sm:h-6" /> :
                           transaction.type === 'withdraw' ? <Upload className="w-5 h-5 sm:w-6 sm:h-6" /> :
                           <Minus className="w-5 h-5 sm:w-6 sm:h-6" />}
                          </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                            {transaction.type === 'buy' ? 'Bought' : 
                             transaction.type === 'sell' ? 'Sold' :
                             transaction.type === 'deposit' ? 'Deposited' :
                             transaction.type === 'withdraw' ? 'Withdrew' :
                             'Unknown'} {transaction.currency_code || transaction.symbol || 'Unknown'}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-500 truncate">
                            {formatCrypto(transaction.crypto_amount || transaction.amount || 0, 6)} {transaction.currency_code || transaction.symbol || 'Unknown'}
                            {(transaction.type === 'buy' || transaction.type === 'sell') && transaction.price_per_unit > 0 ? 
                              ` @ ${formatCurrency(transaction.price_per_unit)}` : ''}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDate(transaction.executed_at || transaction.created_at || new Date())}
                            </p>
                          </div>
                        </div>
                      <div className="text-right flex-shrink-0">
                        {(transaction.type === 'buy' || transaction.type === 'sell') && (
                          <p className={`font-semibold text-sm sm:text-base ${
                            transaction.type === 'buy' ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {transaction.type === 'buy' ? '-' : '+'}
                            {formatCurrency(transaction.usd_amount || transaction.total_value || 0)}
                          </p>
                        )}
                        {(transaction.type === 'withdraw' || transaction.type === 'deposit') && (
                          <p className={`font-semibold text-sm sm:text-base ${
                            transaction.type === 'withdraw' ? 'text-orange-600' : 'text-blue-600'
                          }`}>
                            {transaction.type === 'withdraw' ? '-' : '+'}
                            {formatCrypto(transaction.crypto_amount || transaction.amount || 0, 6)} {transaction.currency_code || transaction.symbol || 'Unknown'}
                          </p>
                        )}
                        {(transaction.fee && transaction.fee > 0) && (
                          <p className="text-xs sm:text-sm text-gray-500">
                            Fee: {(transaction.type === 'buy' || transaction.type === 'sell') ? 
                                  formatCurrency(transaction.fee) : 
                                  `${formatCrypto(transaction.fee, 6)} ${transaction.currency_code || transaction.symbol || 'Unknown'}`}
                          </p>
                        )}
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                            transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                          transaction.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                          transaction.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                          transaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {transaction.status || 'Unknown'}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
            )}
              </motion.div>
            )}
          </div>

      {/* Deposit Modal */}
      <AnimatePresence>
        {showDepositModal && (
            <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDepositModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Deposit Cryptocurrency</h3>
                <button
                  onClick={() => setShowDepositModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleDeposit} className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cryptocurrency
                  </label>
                  <select
                    value={depositData.symbol}
                    onChange={(e) => setDepositData({...depositData, symbol: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  >
                    {depositMethods.map((method) => (
                      <option key={method.symbol} value={method.symbol}>
                        {method.name} ({method.symbol})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount
                  </label>
                  <input
                    type="number"
                    value={depositData.amount}
                    onChange={(e) => setDepositData({...depositData, amount: e.target.value})}
                    placeholder="0.00000000"
                    step="0.00000001"
                    min="0.001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction Hash
                  </label>
                  <input
                    type="text"
                    value={depositData.txHash}
                    onChange={(e) => setDepositData({...depositData, txHash: e.target.value})}
                    placeholder="0x..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Address
                  </label>
                  <input
                    type="text"
                    value={depositData.fromAddress}
                    onChange={(e) => setDepositData({...depositData, fromAddress: e.target.value})}
                    placeholder="Your wallet address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    required
                  />
                </div>

                {/* Deposit Address Info */}
                {depositMethods.find(m => m.symbol === depositData.symbol) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                    <h4 className="font-medium text-blue-900 mb-2 text-sm sm:text-base">Deposit Address</h4>
                    <div className="flex items-center justify-between bg-white rounded-lg p-2 sm:p-3 border">
                      <span className="text-xs sm:text-sm font-mono text-gray-700 break-all flex-1 mr-2">
                        {depositMethods.find(m => m.symbol === depositData.symbol)?.address}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(depositMethods.find(m => m.symbol === depositData.symbol)?.address)}
                        className="p-1 text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
                      >
                        {copiedAddress === depositMethods.find(m => m.symbol === depositData.symbol)?.address ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
                    </div>
                    <p className="text-xs text-blue-700 mt-2">
                      Network: {depositMethods.find(m => m.symbol === depositData.symbol)?.network} • 
                      Min: {depositMethods.find(m => m.symbol === depositData.symbol)?.min_amount} {depositData.symbol}
                    </p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowDepositModal(false)}
                    className="w-full sm:flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base"
                  >
                    Cancel
                </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm sm:text-base"
                  >
                    {loading ? 'Submitting...' : 'Submit Deposit'}
                </button>
              </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Withdraw Modal */}
      <AnimatePresence>
        {showWithdrawModal && (
            <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowWithdrawModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Withdraw Cryptocurrency</h3>
                <button
                  onClick={() => setShowWithdrawModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleWithdraw} className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cryptocurrency
                  </label>
                  <select
                    value={withdrawData.symbol}
                    onChange={(e) => setWithdrawData({...withdrawData, symbol: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  >
                    {portfolio.map((asset) => (
                      <option key={asset.symbol} value={asset.symbol}>
                        {asset.name} ({asset.symbol}) - {formatCrypto(asset.balance, 6)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount
                  </label>
                  <input
                    type="number"
                    value={withdrawData.amount}
                    onChange={(e) => setWithdrawData({...withdrawData, amount: e.target.value})}
                    placeholder="0.00000000"
                    step="0.00000001"
                    min="0.001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available: {formatCrypto(getSelectedCryptoBalance(), 6)} {withdrawData.symbol}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Destination Address
                  </label>
                  <input
                    type="text"
                    value={withdrawData.toAddress}
                    onChange={(e) => setWithdrawData({...withdrawData, toAddress: e.target.value})}
                    placeholder="Destination wallet address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Network (Optional)
                  </label>
                  <input
                    type="text"
                    value={withdrawData.network}
                    onChange={(e) => setWithdrawData({...withdrawData, network: e.target.value})}
                    placeholder="e.g. BTC, ETH, BSC"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  />
              </div>

                {/* Network Fee Warning */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start">
                    <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-yellow-800 text-sm sm:text-base">Network Fee Notice</h4>
                      <p className="text-xs sm:text-sm text-yellow-700 mt-1">
                        A network fee will be deducted from your withdrawal. 
                        Please ensure you have sufficient balance to cover both the withdrawal amount and network fees.
                      </p>
              </div>
              </div>
          </div>

                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowWithdrawModal(false)}
                    className="w-full sm:flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm sm:text-base"
                  >
                    {loading ? 'Processing...' : 'Withdraw'}
                  </button>
        </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CryptoTrading; 